import type {FileDocumentResponse} from "@mytour/shared";
import dayjs from "dayjs";
import {Button, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import {formatBytes, getFileTypeBadge, isImageMime} from "../creation/documentDisplay";
import {DocumentTile} from "./DocumentTile";
import {DocumentDeleteAction} from "./actions/DocumentDeleteAction";
import {DocumentDownloadAction} from "./actions/DocumentDownloadAction";

type Props = {
  doc: FileDocumentResponse;
  previewUrl: string | null;
  size?: number;
  open: boolean;
  onToggle: () => void;
  onResume?: (document: FileDocumentResponse) => void;
};

export function FileTileLightbox({doc, previewUrl, size, open, onToggle, onResume}: Props) {
  const isImage = isImageMime(doc.mimeType);
  const available =
    !doc.uploadStatus || doc.uploadStatus === "legacy" || doc.uploadStatus === "received";
  const uploadLabel =
    doc.uploadStatus === "pending"
      ? "Fichier non reçu"
      : doc.uploadStatus === "invalid"
        ? "Contenu reçu non conforme"
        : "Réception inconnue";
  const badge = !isImage ? getFileTypeBadge(doc.mimeType) : null;
  const authorName = doc.member ? `${doc.member.firstName} ${doc.member.lastName}` : "Inconnu";
  const date = dayjs(doc.createdDate).format("D MMM YYYY · HH:mm");

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        style={{cursor: "pointer"}}
      >
        <DocumentTile
          thumb={
            isImage
              ? {kind: "image", src: previewUrl}
              : {
                  kind: "badge",
                  label: badge?.label ?? "FILE",
                  color: badge?.color ?? "#6c757d",
                }
          }
          title={doc.originalFilename}
          subtitle={available ? formatBytes(doc.sizeBytes) : uploadLabel}
          subtitleClamp={2}
          size={size}
        />
      </div>
      {!available && onResume && (
        <Button size="sm" color="primary" outline onClick={handleResume}>
          Vérifier / reprendre
        </Button>
      )}
      <Modal isOpen={open} toggle={onToggle} size="lg" centered>
        <ModalHeader toggle={onToggle} className="text-break">
          {doc.originalFilename}
        </ModalHeader>
        <ModalBody className="d-flex align-items-center justify-content-center p-0 bg-light">
          {!available ? (
            <div role="status" className="p-4">
              {uploadLabel}
            </div>
          ) : isImage && previewUrl ? (
            <img
              src={previewUrl}
              alt={doc.originalFilename}
              style={{maxWidth: "100%", maxHeight: "75vh", objectFit: "contain"}}
            />
          ) : badge ? (
            <span
              className="text-white rounded my-5 px-5 py-4"
              style={{backgroundColor: badge.color, fontWeight: 700, fontSize: 48}}
            >
              {badge.label}
            </span>
          ) : null}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between align-items-center">
          <div className="text-muted small">
            {formatBytes(doc.sizeBytes)} · {authorName} · {date}
          </div>
          <div className="d-flex gap-2 justify-content-end">
            {available && (
              <DocumentDownloadAction
                documentId={doc.id}
                filename={doc.originalFilename}
                label="Télécharger"
              />
            )}
            {!available && onResume && (
              <Button color="primary" onClick={handleResume}>
                Vérifier / reprendre
              </Button>
            )}
            <DocumentDeleteAction doc={doc} label="Supprimer" />
          </div>
        </ModalFooter>
      </Modal>
    </>
  );

  function handleResume() {
    if (open) {
      onToggle();
    }
    onResume?.(doc);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle();
    }
  }
}
